import { minBy, uniqWith } from 'es-toolkit';

import { notNullish } from '@paragon/utilities';

import Direction3D from './Direction3D';
import LineSegment3D from './LineSegment3D';
import Plane3D from './Plane3D';
import Point3D from './Point3D';
import Polygon3D, { centerPoint3D } from './Polygon3D';
import Polygons from './Polygons';
import { groupPointsTransitively } from './util';

class Polyhedron {
  readonly vertices: readonly Point3D[];
  readonly facesByIndex: readonly (readonly number[])[];

  static fromFaces(polygonList: readonly Polygon3D[]): Polyhedron {
    const { placedFaces, unplacedFaces } = Polyhedron.placeFaces(polygonList);

    if (unplacedFaces.length) throw new Error('Extra faces passed in.');

    // TODO more validation, e.g. for self-intersecting polyhedra
    return this.fromValidatedFaces(placedFaces);
  }

  static manyFromFaces(polygonList: readonly Polygon3D[]) {
    const solids = [];
    let unplacedFaces = polygonList;
    while (unplacedFaces.length) {
      const result = Polyhedron.placeFaces(unplacedFaces);
      unplacedFaces = result.unplacedFaces;
      // TODO more validation, e.g. for self-intersecting polyhedra
      solids.push(Polyhedron.fromValidatedFaces(result.placedFaces));
    }
    return solids;
  }

  private static placeFaces(polygonList: readonly Polygon3D[]): {
    placedFaces: Polygon3D[];
    unplacedFaces: Polygon3D[];
  } {
    const placedFaces: Polygon3D[] = [];
    const unplacedFaces = [...polygonList];
    let edgesWithoutNeighboringFace: LineSegment3D[] = [];

    // find the first (lowest) face and properly orient it (downwards)
    const lowestZVertex = minBy(
      polygonList.flatMap(({ vertices }) => vertices),
      ({ z }) => z,
    )!;
    const bottomFaces = polygonList.filter((face) => face.vertices.some(lowestZVertex.equals.bind(lowestZVertex)));
    if (!bottomFaces) throw new Error('Invalid Polyhedron');
    const lowest = minBy(bottomFaces, (face) => face.normalDirection.smallestAngleBetween(Direction3D.in))!;
    let nextFace = lowest.normalDirection.dotProduct(Direction3D.in) < 0 ? lowest.reverse() : lowest;

    // add face and find next one until polyhedron is assembled
    do {
      const [loopNextFace, loopEdgesWithoutNeighboringFace] = [nextFace, edgesWithoutNeighboringFace];
      placedFaces.push(nextFace);
      unplacedFaces.splice(unplacedFaces.findIndex(loopNextFace.equals.bind(loopNextFace)), 1);
      edgesWithoutNeighboringFace = edgesWithoutNeighboringFace
        .filter((edge) => !loopNextFace.edges.some(edge.equals.bind(edge)))
        .concat(loopNextFace.edges.filter((edge) => !loopEdgesWithoutNeighboringFace.some(edge.equals.bind(edge))));
      if (!edgesWithoutNeighboringFace.length) return { placedFaces, unplacedFaces };
      const currentEdge = edgesWithoutNeighboringFace[0];
      const unorientedNextFace = unplacedFaces
        .map((face) => ({ face, edge: face.edges.find(currentEdge.equals.bind(currentEdge)) }))
        .map((pair) => (pair.edge ? { face: pair.face, edge: pair.edge } : null))
        .filter(notNullish)[0];
      if (unorientedNextFace === undefined) throw new Error('The passed list of faces do not form a closed region.');
      nextFace = currentEdge.basePoint.equals(unorientedNextFace.edge.basePoint)
        ? unorientedNextFace.face.reverse()
        : unorientedNextFace.face;
      // eslint-disable-next-line no-constant-condition
    } while (true);
  }

  private static fromValidatedFaces(faces: readonly Polygon3D[]): Polyhedron {
    const canonicalVertices = groupPointsTransitively(faces.flatMap(({ vertices }) => vertices)).map(centerPoint3D);

    const facesByIndex = faces.map(({ vertices }) =>
      vertices.map((vertex) => canonicalVertices.findIndex(vertex.equals.bind(vertex))),
    );

    return new Polyhedron(canonicalVertices, facesByIndex);
  }

  private constructor(vertices: readonly Point3D[], facesByIndex: readonly (readonly number[])[]) {
    if (facesByIndex.some((face) => face.some((vertexIndex) => vertexIndex >= vertices.length)))
      throw new Error('Index error');

    this.vertices = vertices;
    this.facesByIndex = facesByIndex;
  }

  get faces() {
    return this.facesByIndex.map((face) => new Polygon3D(face.map((vertexIndex) => this.vertices[vertexIndex])));
  }

  get edges() {
    return uniqWith(
      this.faces.flatMap((face) => face.edges),
      (edgeA, edgeB) => edgeA.equals(edgeB),
    );
  }

  equals(other: Polyhedron) {
    return this.faces.every((face) => other.faces.some(face.equals.bind(face)));
  }

  toPolygons(): Polygons {
    return new Polygons(this.faces);
  }

  trimByMany(trimmingPlanes: readonly Plane3D[]): Polyhedron[] {
    return this.toPolygons()
      .trimByMany(trimmingPlanes)
      .map((polygons) => polygons.toPolyhedron());
  }
}

export default Polyhedron;
